import * as Icon from '@mui/icons-material'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import { useTheme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import { keepPreviousData } from '@tanstack/react-query'
import { matchByPath, useLocation } from '@tanstack/react-router'
import * as React from 'react'
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from '../constants'
import PageSidebarContext from '../context/PageSidebarContext'
import { useGetPaymentStatusCountsQuery } from '../generated/graphql'
import { getDrawerSxTransitionMixin, getDrawerWidthTransitionMixin } from '../mixins'
import IconStack from './IconStack'
import PageSidebarDividerItem from './PageSidebarDividerItem'
import PageSidebarHeaderItem from './PageSidebarHeaderItem'
import PageSidebarItem from './PageSidebarPageItem'

export interface PageSidebarProps {
  expanded?: boolean
  setExpanded: (expanded: boolean) => void
  disableCollapsibleSidebar?: boolean
  container?: Element
}

export default function PageSidebar({
  expanded = true,
  setExpanded,
  disableCollapsibleSidebar = false,
  container,
}: PageSidebarProps) {
  const theme = useTheme()

  const { pathname } = useLocation()

  const [_expandedItemIds, setExpandedItemIds] = React.useState<string[]>([])

  const { data: statusCountsData } = useGetPaymentStatusCountsQuery(
    {
      paymentStatus: [
        'OPEN',
        'UNDER_REVIEW',
        'HELD',
        'APPROVED',
        'ENQUEUED_FOR_RELEASE',
        'SUBMITTED',
        'PENDING_BANK_INFO',
        'PROCESSING',
        'FAILED',
        'COMPLETED',
      ],
    },
    {
      placeholderData: keepPreviousData,
      refetchInterval: 30_000,
    }
  )
  const statusCounts = statusCountsData?.getPaymentStatusCounts ?? undefined

  const isOverSmViewport = useMediaQuery(theme.breakpoints.up('sm'))
  const isOverMdViewport = useMediaQuery(theme.breakpoints.up('md'))

  const [isFullyExpanded, setIsFullyExpanded] = React.useState(expanded)
  const [isFullyCollapsed, setIsFullyCollapsed] = React.useState(!expanded)

  React.useEffect(() => {
    if (expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsFullyExpanded(true)
      }, theme.transitions.duration.enteringScreen)

      return () => clearTimeout(drawerWidthTransitionTimeout)
    }

    setIsFullyExpanded(false)

    return () => {}
  }, [expanded, theme.transitions.duration.enteringScreen])

  React.useEffect(() => {
    if (!expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsFullyCollapsed(true)
      }, theme.transitions.duration.leavingScreen)

      return () => clearTimeout(drawerWidthTransitionTimeout)
    }

    setIsFullyCollapsed(false)

    return () => {}
  }, [expanded, theme.transitions.duration.leavingScreen])

  const mini = !disableCollapsibleSidebar && !expanded

  const handleSetSidebarExpanded = React.useCallback(
    (newExpanded: boolean) => () => {
      setExpanded(newExpanded)
    },
    [setExpanded]
  )

  const handlePageItemClick = React.useCallback(
    (itemId: string, hasNestedNavigation: boolean) => {
      if (hasNestedNavigation && !mini) {
        setExpandedItemIds((previousValue: string[]) =>
          previousValue.includes(itemId)
            ? previousValue.filter((previousValueItemId: string) => previousValueItemId !== itemId)
            : [...previousValue, itemId]
        )
      } else if (!isOverSmViewport && !hasNestedNavigation) {
        setExpanded(false)
      }
    },
    [mini, setExpanded, isOverSmViewport]
  )

  const hasDrawerTransitions = isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport)

  const getDrawerContent = React.useCallback(
    (viewport: string) => (
      <React.Fragment>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'auto',
            scrollbarGutter: mini ? 'stable' : 'auto',
            overflowX: 'hidden',
            pt: !mini ? 0 : 2,
            ...(hasDrawerTransitions ? getDrawerSxTransitionMixin(isFullyExpanded, 'padding') : {}),
          }}
        >
          <List
            dense
            sx={{
              padding: mini ? 0 : 0.5,
              mb: 4,
              width: mini ? MINI_DRAWER_WIDTH : 'auto',
            }}
          >
            <PageSidebarHeaderItem icon={<Icon.Payment />}>Payments</PageSidebarHeaderItem>
            <PageSidebarItem
              id="open"
              title="Open"
              icon={<Icon.ImportContacts />}
              href="/payments/status/open"
              selected={!!matchByPath('/payments/status/open', { to: pathname })}
              count={statusCounts?.OPEN}
            />
            <PageSidebarItem
              id="under_review"
              title="Under Review"
              icon={<Icon.Assignment />}
              href="/payments/status/under_review"
              selected={
                !!matchByPath('/payments/status/under_review', {
                  to: pathname,
                })
              }
              count={statusCounts?.UNDER_REVIEW}
            />
            <PageSidebarItem
              id="held"
              title="Held"
              icon={<Icon.Pause />}
              href="/payments/status/held"
              selected={!!matchByPath('/payments/status/held', { to: pathname })}
              count={statusCounts?.HELD}
            />
            <PageSidebarItem
              id="approved"
              title="Approved"
              icon={<Icon.CheckCircleOutline />}
              href="/payments/status/approved"
              selected={
                !!matchByPath('/payments/status/approved', {
                  to: pathname,
                })
              }
              count={statusCounts?.APPROVED}
            />
            <PageSidebarItem
              id="cancelled"
              title="Cancelled"
              icon={<Icon.Block />}
              href="/payments/status/cancelled"
              selected={
                !!matchByPath('/payments/status/cancelled', {
                  to: pathname,
                })
              }
              count={statusCounts?.CANCELLED}
            />
            <PageSidebarItem
              id="enqueued_for_release"
              title="Queued"
              icon={<Icon.Timer />}
              href="/payments/status/enqueued_for_release"
              selected={
                !!matchByPath('/payments/status/enqueued_for_release', {
                  to: pathname,
                })
              }
              count={statusCounts?.ENQUEUED_FOR_RELEASE}
            />
            <PageSidebarItem
              id="submitted"
              title="Submitted"
              icon={<Icon.AssignmentReturned />}
              href="/payments/status/submitted"
              selected={
                !!matchByPath('/payments/status/submitted', {
                  to: pathname,
                })
              }
              count={statusCounts?.SUBMITTED}
            />
            <PageSidebarItem
              id="pending_bank_info"
              title="Pending Bank Info"
              icon={
                <IconStack
                  centerIcon={Icon.AccountBalance}
                  overlayIcons={[{ icon: Icon.Info, position: 'bottomRight' }]}
                />
              }
              href="/payments/status/pending_bank_info"
              selected={
                !!matchByPath('/payments/status/pending_bank_info', {
                  to: pathname,
                })
              }
              count={statusCounts?.PENDING_BANK_INFO}
            />
            <PageSidebarItem
              id="processing"
              title="Processing"
              icon={<Icon.Update />}
              href="/payments/status/processing"
              selected={
                !!matchByPath('/payments/status/processing', {
                  to: pathname,
                })
              }
              count={statusCounts?.PROCESSING}
            />
            <PageSidebarItem
              id="failed"
              title="Failed"
              icon={<Icon.AssignmentLate />}
              href="/payments/status/failed"
              selected={!!matchByPath('/payments/status/failed', { to: pathname })}
              defaultExpanded={true}
              count={statusCounts?.FAILED}
              alwaysExpanded={true}
              nestedNavigation={
                <List
                  dense
                  sx={{
                    padding: 0,
                    my: 1,
                    pl: mini ? 0 : 1,
                    minWidth: DRAWER_WIDTH,
                  }}
                >
                  <PageSidebarItem
                    id="bank_errors"
                    title="Bank Errors"
                    icon={
                      <IconStack
                        centerIcon={Icon.AccountBalance}
                        overlayIcons={[{ icon: Icon.Warning, position: 'bottomRight' }]}
                      />
                    }
                    href="/payments/status/failed_bank"
                    selected={
                      !!matchByPath('/payments/status/failed_bank', {
                        to: pathname,
                      })
                    }
                    count={statusCounts?.FAILED_BREAKDOWN?.BANK_ERROR}
                  />
                  <PageSidebarItem
                    id="api_failures"
                    title="API Failures"
                    icon={<Icon.PowerOff />}
                    href="/payments/status/failed_api"
                    selected={
                      !!matchByPath('/payments/status/failed_api', {
                        to: pathname,
                      })
                    }
                    count={statusCounts?.FAILED_BREAKDOWN?.API_ERROR}
                  />
                  <PageSidebarItem
                    id="unknown_failures"
                    title="Unknown"
                    icon={
                      <IconStack
                        centerIcon={Icon.AssignmentLate}
                        overlayIcons={[
                          {
                            icon: Icon.QuestionMark,
                            position: 'middleCenter',
                            size: '1rem',
                            sx: {
                              backgroundColor: 'rgb(117, 117, 117)',
                              color: '#fff',
                            },
                          },
                        ]}
                      />
                    }
                    href="/payments/status/failed_unknown"
                    selected={
                      !!matchByPath('/payments/status/failed_unknown', {
                        to: pathname,
                      })
                    }
                    count={statusCounts?.FAILED_BREAKDOWN?.UNKNOWN}
                  />
                </List>
              }
            />
            <PageSidebarItem
              id="completed"
              title="Completed"
              icon={<Icon.AssignmentTurnedIn />}
              href="/payments/status/completed"
              selected={
                !!matchByPath('/payments/status/completed', {
                  to: pathname,
                })
              }
              count={statusCounts?.COMPLETED}
            />

            <PageSidebarDividerItem />
            <PageSidebarItem
              id="driver-payments"
              title="Driver Payments"
              icon={<Icon.PersonSearch />}
              href="/driver-payments"
              selected={!!matchByPath('/driver-payments', { to: pathname })}
            />
            <PageSidebarItem
              id="add-ledger-items"
              title="Add Ledger Items"
              icon={<Icon.AddCircleOutline />}
              href="/add-ledger-items"
              selected={!!matchByPath('/add-ledger-items', { to: pathname })}
            />
          </List>
        </Box>
      </React.Fragment>
    ),
    [mini, hasDrawerTransitions, isFullyExpanded, pathname, statusCounts]
  )

  const getDrawerSharedSx = React.useCallback(
    (isTemporary: boolean) => {
      const drawerWidth = mini ? MINI_DRAWER_WIDTH : DRAWER_WIDTH

      return {
        displayPrint: 'none',
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(expanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        '& .MuiDrawer-paper': {
          position: 'absolute',
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(expanded),
        },
      }
    },
    [expanded, mini]
  )

  const sidebarContextValue = React.useMemo(() => {
    return {
      onPageItemClick: handlePageItemClick,
      mini,
      fullyExpanded: isFullyExpanded,
      fullyCollapsed: isFullyCollapsed,
      hasDrawerTransitions,
    }
  }, [handlePageItemClick, mini, isFullyExpanded, isFullyCollapsed, hasDrawerTransitions])

  return (
    <PageSidebarContext.Provider value={sidebarContextValue}>
      <Drawer
        container={container}
        variant="temporary"
        open={expanded}
        onClose={handleSetSidebarExpanded(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            sm: disableCollapsibleSidebar ? 'block' : 'none',
            md: 'none',
          },
          ...getDrawerSharedSx(true),
        }}
      >
        {getDrawerContent('phone')}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: {
            xs: 'none',
            sm: disableCollapsibleSidebar ? 'none' : 'block',
            md: 'none',
          },
          ...getDrawerSharedSx(false),
        }}
      >
        {getDrawerContent('tablet')}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          ...getDrawerSharedSx(false),
        }}
      >
        {getDrawerContent('desktop')}
      </Drawer>
    </PageSidebarContext.Provider>
  )
}
